<script setup lang="ts">
import { ref } from 'vue';

const isModalOpen = defineModel<boolean>('open', { default: false });
const email = ref('');
const isSubmitting = ref(false);
const isSuccess = ref(false);
const errorMessage = ref('');
const config = useRuntimeConfig();

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  // Reset form state when closing
  if (isSuccess.value) {
    setTimeout(() => {
      isSuccess.value = false;
      email.value = '';
      errorMessage.value = '';
    }, 300);
  }
};

const validateEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const handleSubmit = async () => {
  // Reset states
  errorMessage.value = '';
  
  // Validate email
  if (!email.value) {
    errorMessage.value = 'Please enter your email address';
    return;
  }
  
  if (!validateEmail(email.value)) {
    errorMessage.value = 'Please enter a valid email address';
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    const response = await fetch(config.public.baseURL + "/api/newsletters/coupon_subscribe", {
      method: 'POST',
      body: JSON.stringify({
        email: email.value
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })

    const payload = await response.json()

    if (response.ok) {
      isSuccess.value = true;
    } else {
      errorMessage.value = payload?.error || 'Something went wrong. Please try again.';
    }
  } catch (error) {
    errorMessage.value = 'Something went wrong. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

// Close modal when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const modal = document.querySelector('.newsletter-modal');
  if (modal && !modal.contains(event.target as Node)) {
    closeModal();
  }
};
</script>

<template>
  <!-- Sticky Newsletter Button -->
  <div class="w-fit h-fit fixed left-0 top-1/2 transform -translate-y-1/2 z-40">
    <button 
      @click="openModal"
      class="bg-neuroprimary origin-top-left inset-0 block text-white py-1 px-5 transform -rotate-90 hover:bg-opacity-90 transition-all"
    >
      Want 10% off? Subscribe
    </button>
  </div>
  
  <!-- Newsletter Modal -->
  <Teleport to="body">
    <div 
      v-if="isModalOpen" 
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click="handleClickOutside"
    >
      <div class="newsletter-modal bg-neurodarkgrey rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button 
          @click="closeModal"
          class="absolute top-3 right-3 text-gray-400 hover:text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div v-if="!isSuccess">
          <h3 class="text-2xl font-bold text-white mb-2">Get 10% Off Your Order</h3>
          <p class="text-gray-300 mb-6">Subscribe to our newsletter and receive a 10% discount code for your next purchase.</p>
          
          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label for="email" class="block text-gray-300 mb-1">Email Address</label>
              <input 
                type="email" 
                id="email" 
                v-model="email" 
                class="w-full px-4 py-2 rounded bg-neurodarkgreyvar border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-neuroprimary"
                placeholder="your@email.com"
              />
              <p v-if="errorMessage" class="text-red-500 mt-1 text-sm">{{ errorMessage }}</p>
            </div>
            
            <button 
              type="submit" 
              class="w-full bg-neuroprimary text-white py-2 px-4 rounded font-bold hover:bg-opacity-90 transition-all"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">Subscribing...</span>
              <span v-else>Subscribe & Get Discount</span>
            </button>
            
            <p class="text-gray-400 text-xs mt-4">
              By subscribing, you agree to receive marketing emails from Neuronoir. 
              You can unsubscribe at any time.
            </p>
          </form>
        </div>
        
        <div v-else class="text-center py-6">
          <div class="text-green-500 mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-2xl font-bold text-white mb-2">Thank You for Subscribing!</h3>
          <p class="text-gray-300 mb-6">Your discount code has been sent to your email.</p>
          <button 
            @click="closeModal"
            class="bg-neuroprimary text-white py-2 px-6 rounded font-bold hover:bg-opacity-90 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
